import React, { useState } from 'react';
import { useCampaignsConfig } from '../hooks/useCampaignsConfig';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import {
  Building2,
  MousePointer,
  Rocket,
  Layers,
  HelpCircle,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Info,
  Image,
  Link,
  MessageSquare,
  ArrowRightLeft,
  Star,
  Copy,
  Pencil,
  Key,
  Sparkles,
  ListChecks,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { clearOnboardingDismissals } from '../components/GettingStarted';

// ─── Reusable primitives ─────────────────────────────────────────────────────

function Section({ icon: Icon, title, children }) {
  return (
    <div className="mb-8">
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-5 h-5 text-[#00E05E]" />
        <h2 className="text-base font-semibold">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function Step({ number, title, children }) {
  return (
    <div className="flex gap-4 mb-4">
      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-[#00E05E]/10 border border-[#00E05E]/30 flex items-center justify-center">
        <span className="text-xs font-bold text-[#00E05E]">{number}</span>
      </div>
      <div className="flex-1 pt-0.5">
        <p className="font-medium text-sm mb-1">{title}</p>
        <div className="text-sm text-[#A1A1AA] space-y-1">{children}</div>
      </div>
    </div>
  );
}

function InfoBox({ type = 'info', children }) {
  const styles = {
    info: { bg: 'bg-blue-500/5 border-blue-500/20', icon: Info, iconColor: 'text-blue-400' },
    warning: { bg: 'bg-yellow-500/5 border-yellow-500/20', icon: AlertTriangle, iconColor: 'text-yellow-400' },
    success: { bg: 'bg-green-500/5 border-green-500/20', icon: CheckCircle2, iconColor: 'text-green-400' },
    error: { bg: 'bg-red-500/5 border-red-500/20', icon: XCircle, iconColor: 'text-red-400' },
  };
  const s = styles[type];
  const Icon = s.icon;
  return (
    <div className={`flex gap-3 p-3 rounded-lg border ${s.bg} mb-4`}>
      <Icon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${s.iconColor}`} />
      <div className="text-sm text-[#A1A1AA]">{children}</div>
    </div>
  );
}

function Requirement({ met, children }) {
  return (
    <div className="flex items-start gap-2 text-sm mb-1.5">
      {met
        ? <CheckCircle2 className="w-4 h-4 text-[#06C755] flex-shrink-0 mt-0.5" />
        : <XCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />}
      <span className={met ? 'text-[#A1A1AA]' : 'text-red-300'}>{children}</span>
    </div>
  );
}

function Code({ children }) {
  return (
    <code className="px-1.5 py-0.5 rounded bg-[#27272A] text-[#00E05E] font-mono text-xs">
      {children}
    </code>
  );
}

// ─── Tab content ─────────────────────────────────────────────────────────────

function GettingStarted() {
  const showChecklist = () => {
    clearOnboardingDismissals();
    toast.success('The Getting started checklist will show on the Menus page');
  };
  return (
    <div>
      <Section icon={ListChecks} title="Getting started checklist">
        <p className="text-sm text-[#A1A1AA] mb-3">
          New workspaces show a three-step checklist on the <strong className="text-white">Menus</strong> page:
          add a channel, build a menu, publish and set it as default. It disappears once every step is done or when you dismiss it.
        </p>
        <Button size="sm" variant="outline" className="border-[#27272A]" onClick={showChecklist} data-testid="show-checklist-btn">
          Show the checklist again
        </Button>
      </Section>

      <Section icon={Sparkles} title="Plans & limits">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <div className="bg-[#18181B] border border-[#27272A] rounded-lg p-4">
            <p className="font-medium text-sm mb-2">Free</p>
            <ul className="text-sm text-[#A1A1AA] space-y-1">
              <li>• 2 LINE channels per workspace</li>
              <li>• Unlimited menus and drafts</li>
              <li>• Tabbed menus, team invites, activity logs</li>
            </ul>
          </div>
          <div className="bg-[#18181B] border border-[#00E05E]/30 rounded-lg p-4">
            <p className="font-medium text-sm mb-2 text-[#00E05E]">Pro</p>
            <ul className="text-sm text-[#A1A1AA] space-y-1">
              <li>• Unlimited LINE channels</li>
              <li>• Unlimited team members</li>
              <li>• Priority support and early features</li>
            </ul>
          </div>
        </div>
        <p className="text-sm text-[#A1A1AA]">
          To upgrade, click the plan badge next to your workspace name in the sidebar and choose
          <strong className="text-white"> Request Pro</strong>. We enable Pro manually during the beta.
        </p>
      </Section>

      <Section icon={Building2} title="Step 1 — Add a LINE Channel">
        <p className="text-sm text-[#A1A1AA] mb-4">
          Before creating any menus, you need to connect at least one LINE Messaging API channel.
          Go to <strong className="text-white">Channels</strong> in the sidebar.
        </p>
        <Step number="1" title="Get your Channel Access Token">
          <p>Log in to the <strong className="text-white">LINE Developers Console</strong> (developers.line.biz).</p>
          <p>Open your Messaging API channel → <em>Messaging API</em> tab.</p>
          <p>Issue a <strong className="text-white">Long-lived channel access token</strong> and copy it.</p>
        </Step>
        <Step number="2" title="Add the channel here">
          <p>Click <strong className="text-white">Add Channel</strong>, enter a friendly name and paste the token.</p>
          <p>The app validates the token against LINE immediately. A green dot means it works.</p>
        </Step>
        <InfoBox type="warning">
          Keep your channel access token secret. Anyone with this token can control your LINE bot.
          Tokens can be reissued from the LINE Developers Console if compromised.
        </InfoBox>
      </Section>

      <Section icon={Pencil} title="Step 2 — Create a Menu">
        <Step number="1" title="Click Create Menu on the Menus page">
          <p>Select the target channel from the sidebar dropdown first, then open Menus and click Create Menu. The menu stays in that channel: switching channel while editing asks whether to save it as a draft first.</p>
        </Step>
        <Step number="2" title="Enter a name">
          <p>The name field at the top is for your reference only — LINE users won't see it.</p>
        </Step>
        <Step number="3" title="Configure settings">
          <p><strong className="text-white">Chat Bar Text</strong> — the label shown in the LINE chat bar (max 14 chars, e.g. "Menu").</p>
          <p><strong className="text-white">Menu Size</strong> — Full (2500×1686 px) or Compact (2500×843 px).</p>
          <p><strong className="text-white">Menu Alias ID</strong> — optional; needed if other menus switch to this one using richmenuswitch actions.</p>
        </Step>
        <Step number="4" title="Upload an image">
          <p>Go to the Image tab (mobile) or left panel (desktop). Upload a JPEG or PNG.</p>
          <p>The image is previewed immediately; it uploads to storage when you save.</p>
        </Step>
        <Step number="5" title="Draw tap areas on the canvas">
          <p>Click and drag on the canvas to draw rectangular tap areas.</p>
          <p>Click an existing area to select and configure its action.</p>
        </Step>
        <Step number="6" title="Save as draft">
          <p>Click <strong className="text-white">Save Draft</strong>. This saves everything (and uploads the image to storage).</p>
        </Step>
      </Section>

      <Section icon={Rocket} title="Step 3 — Publish to LINE">
        <p className="text-sm text-[#A1A1AA] mb-3">Publishing sends the menu to LINE. The following must all be true:</p>
        <Requirement met={true}>Menu is saved (has an ID)</Requirement>
        <Requirement met={true}>An image has been uploaded</Requirement>
        <Requirement met={true}>At least one tap area is defined</Requirement>
        <Requirement met={true}>Channel access token is valid</Requirement>
        <div className="mt-4">
          <InfoBox type="info">
            When you re-publish an existing menu, LINE deletes the old version and creates a new one automatically.
            If you have an alias set, it is updated to point to the new LINE menu ID.
          </InfoBox>
        </div>
      </Section>
    </div>
  );
}

function BuildingMenus() {
  return (
    <div>
      <Section icon={Image} title="Image Requirements">
        <p className="text-sm text-[#A1A1AA] mb-3">LINE enforces strict image rules. Your image must match one of these exactly:</p>
        <div className="overflow-x-auto mb-4">
          <table className="text-sm w-full">
            <thead>
              <tr className="text-left text-[#52525B] border-b border-[#27272A]">
                <th className="pb-2 pr-6 font-medium">Size</th>
                <th className="pb-2 pr-6 font-medium">Width</th>
                <th className="pb-2 pr-6 font-medium">Height</th>
                <th className="pb-2 font-medium">Use when</th>
              </tr>
            </thead>
            <tbody className="text-[#A1A1AA]">
              {[
                ['Full (recommended)', '2500 px', '1686 px', 'Standard rich menu'],
                ['Full (alternative)', '1200 px', '810 px', 'Smaller file size'],
                ['Compact (recommended)', '2500 px', '843 px', 'Compact menu'],
                ['Compact (alternative)', '1200 px', '405 px', 'Smaller compact'],
              ].map(([size, w, h, note]) => (
                <tr key={size} className="border-b border-[#27272A]/50">
                  <td className="py-2 pr-6 font-mono text-xs text-white">{size}</td>
                  <td className="py-2 pr-6">{w}</td>
                  <td className="py-2 pr-6">{h}</td>
                  <td className="py-2 text-[#71717A]">{note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <InfoBox type="warning">
          <strong>Max file size is 2 MB.</strong> Use JPEG for photos and PNG for graphics with transparency.
          If your image is larger, compress it first (e.g. TinyPNG, Squoosh).
        </InfoBox>
        <InfoBox type="info">
          The canvas auto-adjusts to the image dimensions. Upload the image first so the canvas reflects the correct aspect ratio.
        </InfoBox>
      </Section>

      <Section icon={MousePointer} title="Canvas & Tap Areas">
        <Step number="1" title="Drawing an area">
          <p>Click and drag on the canvas to create a rectangular area. Release to confirm.</p>
          <p>Areas must be at least 20×20 pixels (in menu coordinates) to be saved.</p>
        </Step>
        <Step number="2" title="Selecting an area">
          <p>Click an existing area to select it. The area turns highlighted and the action editor appears.</p>
        </Step>
        <Step number="3" title="Moving an area">
          <p>Click and drag a selected area to reposition it. It stays within canvas bounds automatically.</p>
        </Step>
        <Step number="4" title="Deleting an area">
          <p>Select an area, then click <strong className="text-white">Delete Area</strong> in the action editor panel.</p>
        </Step>
        <InfoBox type="info">
          LINE supports up to <strong className="text-white">20 tap areas</strong> per menu.
          Areas can overlap — LINE processes the first matching area from the list.
        </InfoBox>
      </Section>

      <Section icon={MessageSquare} title="Action Types">
        <div className="space-y-4">
          {[
            {
              icon: MessageSquare,
              label: 'Message',
              color: 'text-blue-400',
              badge: 'Most common',
              desc: 'Sends a text message to the chat on behalf of the user when they tap the area.',
              fields: ['Text — the message to send (required)'],
            },
            {
              icon: Link,
              label: 'URI (Link)',
              color: 'text-purple-400',
              badge: null,
              desc: 'Opens a URL in the LINE browser or an external app when tapped.',
              fields: ['URI — must start with https://, tel:, or line://'],
            },
            {
              icon: ArrowRightLeft,
              label: 'Rich Menu Switch',
              color: 'text-orange-400',
              badge: 'Advanced',
              desc: 'Switches the user to a different rich menu when tapped. Useful for multi-page menus.',
              fields: [
                'Rich Menu Alias ID — the alias of the target menu (not its name)',
                'Data — arbitrary string sent as a postback event (required by LINE)',
              ],
            },
            {
              icon: Key,
              label: 'Postback',
              color: 'text-cyan-400',
              badge: null,
              desc: 'Sends a postback event to your webhook silently (the user does not see it in chat).',
              fields: [
                'Data — the postback data string your webhook receives',
                'Text — optional display text shown in chat',
              ],
            },
          ].map(({ icon: Icon, label, color, badge, desc, fields }) => (
            <div key={label} className="p-4 rounded-lg border border-[#27272A] bg-[#18181B]">
              <div className="flex items-center gap-2 mb-2">
                <Icon className={`w-4 h-4 ${color}`} />
                <span className="font-medium text-sm">{label}</span>
                {badge && <Badge variant="outline" className="text-xs border-[#3F3F46] text-[#71717A]">{badge}</Badge>}
              </div>
              <p className="text-sm text-[#A1A1AA] mb-2">{desc}</p>
              <ul className="text-xs text-[#71717A] space-y-0.5">
                {fields.map(f => <li key={f} className="flex items-start gap-1.5"><span className="text-[#52525B] mt-0.5">›</span>{f}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

function PublishingAliases() {
  return (
    <div>
      <Section icon={Rocket} title="What Happens When You Publish">
        <div className="space-y-3 text-sm text-[#A1A1AA]">
          {[
            ['1', 'If a previous LINE menu exists for this entry, it is deleted from LINE first.'],
            ['2', 'A new rich menu is created on LINE and a LINE Menu ID (richMenuId) is returned.'],
            ['3', 'The menu image is fetched from storage and uploaded to LINE using that ID.'],
            ['4', 'If a Menu Alias ID is configured, the alias is created or updated on LINE automatically.'],
            ['5', 'If the menu was previously set as the channel default, the default is automatically re-linked to the new LINE Menu ID — you do not need to set it as default again.'],
            ['6', 'The menu status in the app changes to Published.'],
          ].map(([n, text]) => (
            <div key={n} className="flex gap-3">
              <span className="text-[#00E05E] font-mono font-bold flex-shrink-0">{n}.</span>
              <p>{text}</p>
            </div>
          ))}
        </div>
        <InfoBox type="warning" className="mt-4">
          Publishing can take a few seconds. Do not close the page until you see the success message.
        </InfoBox>
      </Section>

      <Section icon={ArrowRightLeft} title="Menu Aliases Explained">
        <p className="text-sm text-[#A1A1AA] mb-4">
          A <strong className="text-white">Menu Alias</strong> is a stable human-readable identifier (e.g. <Code>richmenu-main</Code>) that
          points to a LINE Menu ID behind the scenes. Aliases allow you to:
        </p>
        <div className="space-y-2 mb-4">
          {[
            'Switch between menus without hardcoding LINE Menu IDs in your bot code.',
            'Update which menu an alias points to just by republishing — no code changes needed.',
            'Use richmenuswitch actions that target an alias instead of a specific menu ID.',
          ].map(t => (
            <div key={t} className="flex items-start gap-2 text-sm text-[#A1A1AA]">
              <CheckCircle2 className="w-4 h-4 text-[#06C755] flex-shrink-0 mt-0.5" />
              {t}
            </div>
          ))}
        </div>
        <InfoBox type="info">
          Alias IDs must be unique per channel. Only letters, numbers, hyphens, and underscores are allowed.
          Example: <Code>richmenu-home</Code>, <Code>richmenu-shop</Code>.
        </InfoBox>
        <InfoBox type="success">
          When you republish a menu that already has an alias, the alias is <strong>automatically updated</strong> to
          point to the new LINE Menu ID. You do not need to manually delete and recreate it.
        </InfoBox>
      </Section>

      <Section icon={Star} title="Setting a default menu">
        <p className="text-sm text-[#A1A1AA] mb-3">
          The default menu is shown to all LINE users in the channel who have not been assigned a specific menu.
          Only one menu can be the default at a time.
        </p>
        <Step number="1" title="Publish the menu first">
          <p>A menu must be published to LINE before it can be set as default.</p>
        </Step>
        <Step number="2" title="Set as default from the Menus page">
          <p>Open the menu's <strong className="text-white">⋯ menu</strong> and click <strong className="text-white">Set as default</strong>.</p>
        </Step>
        <Step number="3" title="Verify in LINE">
          <p>Open a fresh chat with your LINE bot — the default menu should appear at the bottom.</p>
        </Step>
        <InfoBox type="info">
          Setting a default menu sends <Code>POST /user/all/richmenu/{'{'}richMenuId{'}'}</Code> to LINE,
          which applies the menu to all users immediately.
        </InfoBox>
      </Section>
    </div>
  );
}

function Campaigns() {
  const { rollbackDays, maxLatenessMinutes } = useCampaignsConfig();
  const windowText = rollbackDays ? `${rollbackDays} day${rollbackDays === 1 ? '' : 's'}` : 'the rollback window';
  const latenessText = maxLatenessMinutes ? `${maxLatenessMinutes} minute${maxLatenessMinutes === 1 ? '' : 's'}` : 'the allowed delay';
  return (
    <div>
      <Section icon={Rocket} title="Campaigns (Pro)">
        <div className="space-y-3 text-sm text-[#A1A1AA]">
          <p>
            A <strong className="text-white">campaign</strong> is an ordered set of menus on one channel (for example the tabs of a seasonal menu), with an optional default menu.
            You launch them together — now or at a scheduled time — instead of publishing and linking each menu by hand. Free workspaces can see the Campaigns page but can't create campaigns.
          </p>
          <p>
            <strong className="text-white">Keep aliases the same.</strong> People who already opened your chat keep using the alias, so every menu in a campaign needs an alias, and switch actions must point at aliases that exist.
          </p>
          <p>
            <strong className="text-white">Replace a live menu.</strong> Use <em>Replace a live menu</em> to swap a menu that is live today for its new version. The new menu takes over the same alias; the old one is marked <Badge variant="outline" className="text-xs">Replaced</Badge>.
            If a campaign replaces the current default, choose which menu becomes the new default.
          </p>
          <p>
            <strong className="text-white">Check.</strong> Before launching, run <em>Check</em>. It verifies aliases are present, unique and valid, switch targets exist, images can be downloaded, and that LINE accepts every menu.
          </p>
          <p>
            <strong className="text-white">Launch now vs Schedule.</strong> Launch now asks you to type the channel name to confirm. Schedule uses your local time and a time zone you pick.
            If a launch would start more than {latenessText} late (for example the server was down), it is skipped as "missed" and you get an email. If the checks fail within 24 hours of the launch time you get a warning email.
            A live campaign can't be scheduled (it would end its rollback window) — use Launch now. When a scheduled launch starts, the person who scheduled it must still have access to the channel, otherwise it fails without changing anything.
          </p>
          <p>
            <strong className="text-white">If something fails.</strong> New menus are staged invisibly on LINE first, then aliases and the default are switched. If anything fails, LINE is put back as it was and nothing changes for your users. If you relaunch a live campaign and that launch fails, the campaign stays live and can still be rolled back.
            In the rare case that putting it back also fails, the run is flagged <em>Needs attention</em>.
          </p>
          <p>
            <strong className="text-white">Roll back within {windowText}.</strong> Replaced menus (including the previous default menu) are kept on LINE for {windowText}. Click <em>Roll back</em> (type the channel name to confirm) to restore them in one step. If a rollback is interrupted part-way, press Roll back again to finish it.
            Roll back is refused if someone changed the campaign's aliases or the default menu on LINE since — the campaign stays <em>Live</em>. A campaign shows as <em>Superseded</em> when a newer campaign launch replaced its menus; it can no longer be rolled back.
          </p>
          <p>
            While a launch runs, publishing, saving menu edits, setting a default, changing aliases and deleting on that channel are paused ("A campaign is launching on this channel"). Menus kept for rollback can't be deleted during {windowText}, and republishing a menu never removes them from LINE.
          </p>
          <InfoBox type="warning">
            Per-user menus you linked to individual people still override the default menu. Phones may also show the change a little later than the launch.
          </InfoBox>
        </div>
      </Section>
    </div>
  );
}

function Features() {
  return (
    <div>
      <Section icon={Copy} title="Duplicate & Copy to Channel">
        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <div className="p-4 rounded-lg border border-[#27272A] bg-[#18181B]">
            <p className="font-medium text-sm mb-2 flex items-center gap-2">
              <Copy className="w-4 h-4 text-[#A1A1AA]" />
              Duplicate (same channel)
            </p>
            <p className="text-sm text-[#A1A1AA]">
              Creates an identical copy of the menu in the same channel as a draft.
              Alias is not copied (aliases must be unique).
            </p>
          </div>
          <div className="p-4 rounded-lg border border-[#27272A] bg-[#18181B]">
            <p className="font-medium text-sm mb-2 flex items-center gap-2">
              <ArrowRightLeft className="w-4 h-4 text-[#A1A1AA]" />
              Copy to Channel
            </p>
            <p className="text-sm text-[#A1A1AA]">
              Copies the menu (including image URL, areas, settings) to a different channel as a draft.
              Useful for promoting a menu from test to production.
            </p>
          </div>
        </div>
        <InfoBox type="info">
          Both operations copy the stored image URL. If you later delete the image from storage the copy will also lose its image.
          Always publish the copy to LINE after moving it to the production channel.
        </InfoBox>
      </Section>

      <Section icon={Layers} title="Managing Multiple Channels">
        <p className="text-sm text-[#A1A1AA] mb-3">
          A common workflow for teams with test and production LINE bots:
        </p>
        <div className="space-y-3">
          {[
            ['Design & test', 'Create menus in your test channel, draw areas, configure actions, upload images.'],
            ['Verify in LINE', 'Publish to the test channel. Open your LINE bot to check layout and actions.'],
            ['Copy to production', 'Use Copy to Channel to move the menu to the production channel.'],
            ['Publish production', 'Switch to the production channel in the sidebar, open the copied menu, and publish.'],
            ['Set default', 'Set the published production menu as default for all users.'],
          ].map(([title, desc], i) => (
            <div key={title} className="flex gap-3">
              <span className="text-[#00E05E] font-mono text-sm font-bold flex-shrink-0 w-5">{i + 1}.</span>
              <div>
                <p className="text-sm font-medium">{title}</p>
                <p className="text-sm text-[#A1A1AA]">{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section icon={Layers} title="Activity Logs">
        <p className="text-sm text-[#A1A1AA] mb-3">
          Superadmins and workspace admins can view the <strong className="text-white">Activity Logs</strong> page
          (accessible from the sidebar under Workspace) to see a full audit trail of every significant action:
          menu publishes, default changes, channel changes, alias operations, and more.
          Workspace admins see only their own workspace's logs.
        </p>
        <div className="space-y-2">
          {[
            'Each log entry shows: timestamp, user, action type, resource name, and status (success/error).',
            'Click any entry to expand full details including the exact LINE API payload sent and response received.',
            'Filter by action type (e.g. only show publish events) or status (e.g. only errors).',
            'Publish logs include every step: menu creation, image upload, alias update, and default re-link — each with its own LINE status code and response body.',
            'Use this to debug failed publishes — the step that failed and LINE\'s error message are recorded.',
          ].map(t => (
            <div key={t} className="flex items-start gap-2 text-sm text-[#A1A1AA]">
              <CheckCircle2 className="w-4 h-4 text-[#06C755] flex-shrink-0 mt-0.5" />
              {t}
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

function Troubleshooting() {
  const items = [
    {
      q: 'Menu is not showing in the LINE app after publishing',
      a: (
        <div className="space-y-2 text-sm text-[#A1A1AA]">
          <p>Check the following in order:</p>
          <Requirement met={true}>Is the menu status "Published" in the Menus page?</Requirement>
          <Requirement met={true}>Is the channel access token still valid? Go to Channels → click the channel → click the Validate (↻) button.</Requirement>
          <Requirement met={true}>Did you set the menu as default, or link it to specific users?</Requirement>
          <Requirement met={true}>Try closing and reopening the LINE chat — menus sometimes take a moment to refresh.</Requirement>
          <p className="mt-2">If it still doesn't appear, check the Activity Logs for the publish event and expand the details to see the exact LINE API response.</p>
        </div>
      ),
    },
    {
      q: 'Image upload fails or "Image upload failed" on publish',
      a: (
        <div className="space-y-2 text-sm text-[#A1A1AA]">
          <Requirement met={true}>File must be JPEG or PNG — not WebP, GIF, or HEIC.</Requirement>
          <Requirement met={true}>File size must be under 2 MB.</Requirement>
          <Requirement met={true}>Image dimensions must exactly match one of the supported sizes (2500×1686, 2500×843, 1200×810, 1200×405).</Requirement>
          <p>If the image uploads but fails during publish, check that cloud storage is configured and publicly accessible.</p>
        </div>
      ),
    },
    {
      q: 'Rich Menu Switch action is not working',
      a: (
        <div className="space-y-2 text-sm text-[#A1A1AA]">
          <p>For richmenuswitch to work, all of the following must be true:</p>
          <Requirement met={true}>Both menus must be published to the same LINE channel.</Requirement>
          <Requirement met={true}>The target menu must have a Menu Alias ID set.</Requirement>
          <Requirement met={true}>The action's Alias ID field must match the target menu's alias exactly (case-sensitive).</Requirement>
          <Requirement met={true}>The alias must exist on LINE — this happens automatically when you publish.</Requirement>
          <p>Check Activity Logs for <Code>alias.create</Code> or <Code>alias.update</Code> events to confirm the alias was set up.</p>
        </div>
      ),
    },
    {
      q: '"Failed to create alias on LINE" error on publish',
      a: (
        <div className="space-y-2 text-sm text-[#A1A1AA]">
          <p>This usually means the alias ID is already in use by a different menu on LINE that our app doesn't know about.</p>
          <Requirement met={false}>Cannot have two different menus with the same alias ID in the same channel.</Requirement>
          <p>Solutions:</p>
          <p>1. Use a different alias ID for this menu.</p>
          <p>2. Delete the conflicting alias from the LINE Developers Console manually, then republish.</p>
          <p>3. Check if another menu in this app has the same alias ID.</p>
        </div>
      ),
    },
    {
      q: 'Channel token shows as invalid',
      a: (
        <div className="space-y-2 text-sm text-[#A1A1AA]">
          <Requirement met={true}>Make sure you are using a Messaging API channel token, not a Login or other channel type.</Requirement>
          <Requirement met={true}>Long-lived tokens don't expire, but short-lived tokens (30 days) may have expired — reissue from the LINE Developers Console.</Requirement>
          <Requirement met={true}>The channel must have the Messaging API product added (not just LINE Login).</Requirement>
          <p>To update the token: go to <strong className="text-white">Channels</strong>, click the channel card, then click the Edit (pencil) button. After saving, the app revalidates automatically.</p>
        </div>
      ),
    },
    {
      q: 'I published successfully but the old menu still shows',
      a: (
        <div className="space-y-2 text-sm text-[#A1A1AA]">
          <p>LINE caches rich menus on the client side. Try:</p>
          <p>1. Force-quit and reopen the LINE app — this is the most common fix.</p>
          <p>2. Check <strong className="text-white">Channels → (your channel) → LINE Status</strong>. The "Default menu" section shows what LINE actually has set as the default. If it says no default is set, go back and click Set as default.</p>
          <p>3. <strong className="text-white">Per-user override</strong>: a user-level menu assignment overrides the channel default. In the Channel detail page, scroll to "Check user override", enter the user's LINE user ID (<Code>U...</Code>), and click Check. If an override is shown, click Clear override.</p>
          <p>4. Wait up to 60 seconds — LINE sometimes takes time to propagate. Beyond that it is not a cache issue.</p>
          <p>5. Check Activity Logs to confirm the publish and set-default steps both completed with <Code>lineStatus: 200</Code>.</p>
        </div>
      ),
    },
    {
      q: 'A specific user sees a different menu than everyone else',
      a: (
        <div className="space-y-2 text-sm text-[#A1A1AA]">
          <p>This is caused by a per-user menu override. LINE allows assigning a specific menu to individual users, which takes priority over the channel default.</p>
          <p>To diagnose and fix:</p>
          <p>1. Go to <strong className="text-white">Channels</strong> and click the channel card.</p>
          <p>2. Scroll to <strong className="text-white">Check user override</strong> in the LINE Status section.</p>
          <p>3. Enter the user's LINE user ID (<Code>U...</Code>) and click <strong className="text-white">Check</strong>.</p>
          <p>4. If an override is found, click <strong className="text-white">Clear override</strong> — the channel default will apply immediately.</p>
          <InfoBox type="info">
            You can find a user's LINE user ID by setting the Webhook URL on the channel to the one shown in the channel detail page,
            then having the user send any message to the bot. Their ID will appear in the "Users captured via webhook" list.
          </InfoBox>
        </div>
      ),
    },
    {
      q: 'Can I edit a published menu without losing the alias?',
      a: (
        <div className="space-y-2 text-sm text-[#A1A1AA]">
          <p>Yes. The workflow is:</p>
          <p>1. Edit the menu (areas, image, settings) in the builder.</p>
          <p>2. Save the draft — the menu status resets to Draft in the app, but the old menu is still live on LINE.</p>
          <p>3. Republish — the app deletes the old LINE menu, creates a new one, and updates the alias to point to the new menu ID automatically.</p>
          <InfoBox type="success">The alias ID stays the same; only the underlying LINE Menu ID changes. No richmenuswitch actions need updating.</InfoBox>
        </div>
      ),
    },
    {
      q: 'What is the difference between Menu Name and Chat Bar Text?',
      a: (
        <div className="space-y-2 text-sm text-[#A1A1AA]">
          <p><strong className="text-white">Menu Name</strong> — internal label only. Used to identify the menu in the Menus page. LINE users never see it.</p>
          <p><strong className="text-white">Chat Bar Text</strong> — the text shown in the LINE chat keyboard bar that users tap to open/close the menu (max 14 characters). This is visible to users.</p>
        </div>
      ),
    },
  ];

  return (
    <div>
      <p className="text-sm text-[#A1A1AA] mb-6">
        Common questions and issues. Click a question to expand the answer.
      </p>
      <Accordion type="multiple" className="space-y-2">
        {items.map(({ q, a }, i) => (
          <AccordionItem
            key={i}
            value={`item-${i}`}
            className="border border-[#27272A] rounded-lg px-4 data-[state=open]:border-[#3F3F46]"
          >
            <AccordionTrigger className="text-sm font-medium text-left hover:no-underline py-4">
              {q}
            </AccordionTrigger>
            <AccordionContent className="pb-4">
              {a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function Help() {
  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <HelpCircle className="w-6 h-6 text-[#00E05E]" />
        <h1 className="text-xl font-bold">Help & Documentation</h1>
      </div>
      <p className="text-sm text-[#71717A] mb-6">
        Everything you need to create, publish, and manage LINE Rich Menus.
      </p>

      <Tabs defaultValue="getting-started">
        <TabsList className="grid grid-cols-2 sm:grid-cols-4 h-auto bg-[#18181B] mb-6">
          <TabsTrigger value="getting-started" className="text-xs sm:text-sm data-[state=active]:bg-[#27272A] py-2">
            Getting Started
          </TabsTrigger>
          <TabsTrigger value="building" className="text-xs sm:text-sm data-[state=active]:bg-[#27272A] py-2">
            Building Menus
          </TabsTrigger>
          <TabsTrigger value="publishing" className="text-xs sm:text-sm data-[state=active]:bg-[#27272A] py-2">
            Publishing
          </TabsTrigger>
          <TabsTrigger value="troubleshooting" className="text-xs sm:text-sm data-[state=active]:bg-[#27272A] py-2">
            Troubleshooting
          </TabsTrigger>
        </TabsList>

        <TabsContent value="getting-started"><GettingStarted /></TabsContent>
        <TabsContent value="building"><BuildingMenus /></TabsContent>
        <TabsContent value="publishing">
          <PublishingAliases />
          <div className="mt-2"><Features /></div>
          <div className="mt-2"><Campaigns /></div>
        </TabsContent>
        <TabsContent value="troubleshooting"><Troubleshooting /></TabsContent>
      </Tabs>
    </div>
  );
}

export default Help;
